/* =============================================================================
   TINCTURESCU — T3K app.js
   Single IIFE, ES2018, no dependencies. Exposes window.T3K (see BRIEF 2.3).
   Sections: 01 core & helpers · 02 money / stripEmoji / toast · 03 scroll lock &
   focus trap · 04 reveal / stagger / split · 05 counter · 06 marquee · 07 magnetic /
   tilt / spotlight · 08 parallax & scroll progress · 09 cursor · 10 cart API ·
   11 cart drawer & handlers · 12 predictive search · 13 mobile menu · 14 header ·
   15 loader · 16 page transitions · 17 accordion · 18 lightbox · 19 rail ·
   20 anchors · 21 init & boot
   ============================================================================= */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------------
     01 CORE & HELPERS
     --------------------------------------------------------------------------- */
  var W = window, D = document, root = D.documentElement;
  var T3K = W.T3K = W.T3K || {};
  var reducedMQ = W.matchMedia ? W.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var fineMQ = W.matchMedia ? W.matchMedia('(pointer: fine)') : null;
  T3K.reduced = !!(reducedMQ && reducedMQ.matches);
  T3K.fine = !!(fineMQ && fineMQ.matches);
  if (reducedMQ && reducedMQ.addEventListener) {
    reducedMQ.addEventListener('change', function (e) { T3K.reduced = e.matches; });
  }
  T3K.design = !!(W.Shopify && W.Shopify.designMode);
  T3K.routes = T3K.routes || {};
  var R = T3K.routes;
  R.cart = R.cart || '/cart';
  R.cartAdd = R.cartAdd || '/cart/add';
  R.cartChange = R.cartChange || '/cart/change';
  R.cartUpdate = R.cartUpdate || '/cart/update';
  R.search = R.search || '/search';
  R.predictive = R.predictive || '/search/suggest';
  R.root = R.root || '/';
  T3K.strings = T3K.strings || {};
  T3K.settings = T3K.settings || {};
  T3K.version = '1.0.0';

  function S(key, fallback) { return T3K.strings[key] || fallback; }
  function qs(sel, ctx) { return (ctx || D).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || D).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el && el.addEventListener) el.addEventListener(ev, fn, opts || false); }
  function clamp(n, a, b) { return Math.min(b, Math.max(a, n)); }
  function raf(fn) { return W.requestAnimationFrame ? W.requestAnimationFrame(fn) : setTimeout(fn, 16); }
  function debounce(fn, ms) {
    var t;
    return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); };
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function dispatch(name, detail, target) {
    try { (target || D).dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true })); } catch (e) { /* noop */ }
  }
  /* idempotency: each primitive marks the element with a token in data-t3k-inited */
  function once(el, key) {
    if (!el || !el.getAttribute) return false;
    var v = el.getAttribute('data-t3k-inited') || '';
    var list = v ? v.split(' ') : [];
    if (list.indexOf(key) > -1) return false;
    list.push(key);
    el.setAttribute('data-t3k-inited', list.join(' '));
    return true;
  }
  /* all matches inside scope, including scope itself */
  function scopeAll(scope, sel) {
    var list = qsa(sel, scope);
    if (scope !== D && scope.matches && scope.matches(sel)) list.unshift(scope);
    return list;
  }
  function headerH() {
    var v = parseFloat(getComputedStyle(root).getPropertyValue('--header-h'));
    return isNaN(v) ? 72 : v;
  }
  var ICONS = {
    close: '<svg class="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    drop: '<svg class="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3s-6.5 7.2-6.5 11.5a6.5 6.5 0 0 0 13 0C18.5 10.2 12 3 12 3Z"/></svg>'
  };

  /* ---------------------------------------------------------------------------
     02 MONEY / STRIP EMOJI / TOAST
     --------------------------------------------------------------------------- */
  T3K.money = function (cents) {
    var fmt = String(T3K.moneyFormat || '{{amount_with_comma_separator}} lei').replace(/<[^>]*>/g, '');
    var n = Number(cents);
    if (isNaN(n)) n = 0;
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = m ? m[1] : 'amount';
    var dec = 2, thousands = ',', decimal = '.';
    switch (key) {
      case 'amount_no_decimals': dec = 0; break;
      case 'amount_with_comma_separator': thousands = '.'; decimal = ','; break;
      case 'amount_no_decimals_with_comma_separator': dec = 0; thousands = '.'; break;
      case 'amount_with_apostrophe_separator': thousands = "'"; break;
      case 'amount_no_decimals_with_space_separator': dec = 0; thousands = ' '; break;
      case 'amount_with_space_separator': thousands = ' '; decimal = ','; break;
      case 'amount_with_period_and_space_separator': thousands = ' '; decimal = '.'; break;
      default: break;
    }
    var v = (Math.abs(n) / 100).toFixed(dec);
    var parts = v.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    var out = parts[0] + (dec ? decimal + parts[1] : '');
    if (n < 0) out = '-' + out;
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out).trim();
  };

  var EMOJI_RE;
  try {
    /* Extended_Pictographic minus (c) (R) TM, plus flags, skin tones, tag chars, variation selectors, ZWJ, keycap */
    EMOJI_RE = new RegExp('(?![\\u00A9\\u00AE\\u2122])[\\p{Extended_Pictographic}\\u2713\\u2714\\u2705\\u2611\\u2717\\u2718\\u{1F1E6}-\\u{1F1FF}\\u{1F3FB}-\\u{1F3FF}\\u{E0020}-\\u{E007F}]|[\\uFE0E\\uFE0F\\u200D\\u20E3]', 'gu');
  } catch (e) {
    EMOJI_RE = /[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27BF]|[\u2B00-\u2BFF]|[\uFE0E\uFE0F\u200D\u20E3]/g;
  }
  T3K.stripEmoji = function (el) {
    if (!el) return;
    var walker = D.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (n) {
      var t = n.nodeValue;
      if (!t) return;
      EMOJI_RE.lastIndex = 0;
      if (EMOJI_RE.test(t)) {
        EMOJI_RE.lastIndex = 0;
        n.nodeValue = t.replace(EMOJI_RE, '').replace(/[ \t]{2,}/g, ' ');
      }
    });
  };
  T3K.stripEmojiText = function (text) {
    EMOJI_RE.lastIndex = 0;
    return String(text == null ? '' : text).replace(EMOJI_RE, '');
  };

  T3K.toast = function (text, kind, ms) {
    var wrap = qs('#toast');
    if (!wrap) {
      wrap = D.createElement('div');
      wrap.id = 'toast';
      wrap.className = 'toast-wrap';
      wrap.setAttribute('aria-live', 'polite');
      D.body.appendChild(wrap);
    }
    var t = D.createElement('div');
    t.className = 'toast toast--' + (kind || 'info');
    t.setAttribute('role', 'status');
    t.innerHTML = '<span class="toast__dot" aria-hidden="true"></span><span class="toast__text"></span>';
    t.lastChild.textContent = T3K.stripEmojiText(text);
    wrap.appendChild(t);
    while (wrap.children.length > 3) wrap.removeChild(wrap.firstChild);
    setTimeout(function () {
      t.classList.add('is-leaving');
      setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 420);
    }, ms || 3200);
    return t;
  };

  /* ---------------------------------------------------------------------------
     03 SCROLL LOCK & FOCUS TRAP
     --------------------------------------------------------------------------- */
  var lockCount = 0;
  function lockScroll() {
    if (lockCount++ > 0) return;
    var sbw = W.innerWidth - root.clientWidth;
    root.style.setProperty('--sbw', (sbw > 0 ? sbw : 0) + 'px');
    root.classList.add('is-locked');
  }
  function unlockScroll() {
    if (lockCount <= 0) return;
    if (--lockCount > 0) return;
    root.classList.remove('is-locked');
    root.style.setProperty('--sbw', '0px');
  }
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function visible(el) { return !!(el.getClientRects && el.getClientRects().length); }
  function trapFocus(container) {
    var handler = function (e) {
      if (e.key !== 'Tab') return;
      var els = qsa(FOCUSABLE, container).filter(visible);
      if (!els.length) { e.preventDefault(); return; }
      var first = els[0], last = els[els.length - 1];
      if (e.shiftKey && (D.activeElement === first || !container.contains(D.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (D.activeElement === last || !container.contains(D.activeElement))) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return function () { container.removeEventListener('keydown', handler); };
  }
  function focusFirst(container, delay) {
    setTimeout(function () {
      var f = qsa(FOCUSABLE, container).filter(visible)[0] || container;
      try { f.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }, delay || 80);
  }

  /* ---------------------------------------------------------------------------
     04 REVEAL / STAGGER / SPLIT
     --------------------------------------------------------------------------- */
  var revealIO = null;
  function getIO() {
    if (revealIO) return revealIO;
    if (!('IntersectionObserver' in W)) return null;
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var tall = en.boundingClientRect.height > W.innerHeight * 0.6;
        if (en.intersectionRatio >= 0.15 || tall) {
          en.target.classList.add('is-in');
          revealIO.unobserve(en.target);
          if (en.target._t3kCounter) { en.target._t3kCounter(); en.target._t3kCounter = null; }
        }
      });
    }, { threshold: [0, 0.15, 0.3], rootMargin: '0px 0px -6% 0px' });
    return revealIO;
  }
  function observe(el) {
    var io = getIO();
    if (io) io.observe(el);
    else { el.classList.add('is-in'); if (el._t3kCounter) el._t3kCounter(); }
  }
  function splitText(el) {
    var mode = el.getAttribute('data-split') || 'words';
    var plain = el.textContent.replace(/\s+/g, ' ').trim();
    if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', plain);
    var idx = 0;
    function wrapNode(node) {
      var parts = node.nodeValue.split(/(\s+)/);
      var frag = D.createDocumentFragment();
      parts.forEach(function (w) {
        if (!w) return;
        if (/^\s+$/.test(w)) { frag.appendChild(D.createTextNode(' ')); return; }
        var sw = D.createElement('span');
        sw.className = 'sw';
        sw.setAttribute('aria-hidden', 'true');
        if (mode === 'chars') {
          Array.from(w).forEach(function (ch) {
            var sc = D.createElement('span');
            sc.className = 'sc';
            sc.textContent = ch;
            sc.style.setProperty('--i', idx++);
            sw.appendChild(sc);
          });
        } else {
          sw.textContent = w;
          sw.style.setProperty('--i', idx++);
        }
        frag.appendChild(sw);
      });
      node.parentNode.replaceChild(frag, node);
    }
    var walker = D.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) { if (walker.currentNode.nodeValue.trim()) nodes.push(walker.currentNode); }
    nodes.forEach(wrapNode);
    if (mode === 'lines') {
      var lastTop = null, li = -1;
      qsa('.sw', el).forEach(function (sw) {
        var top = sw.offsetTop;
        if (lastTop === null || Math.abs(top - lastTop) > 2) { li++; lastTop = top; }
        sw.style.setProperty('--i', li);
      });
    }
    el.classList.add('is-split');
  }

  /* ---------------------------------------------------------------------------
     05 COUNTER
     --------------------------------------------------------------------------- */
  function initCounter(el) {
    var rawTarget = String(el.getAttribute('data-counter') || '');
    var target = parseFloat(rawTarget.replace(',', '.'));
    if (isNaN(target)) return;
    var suffix = el.getAttribute('data-suffix') || '';
    var orig = el.textContent;
    var m = orig.match(/\d[\d.,]*/);
    var numStr = m ? m[0] : null;
    var dec = (rawTarget.split(/[.,]/)[1] || '').length;
    var dur = parseInt(el.getAttribute('data-duration') || '1400', 10);
    function fmt(v) {
      var s = dec ? v.toFixed(dec) : Math.round(v).toString();
      if (numStr && numStr.indexOf(',') > -1 && numStr.indexOf('.') === -1) s = s.replace('.', ',');
      return s;
    }
    function render(v, done) {
      var s = fmt(v) + (done ? suffix : '');
      el.textContent = numStr ? orig.replace(numStr, s) : s;
    }
    var from = parseFloat(String(el.getAttribute('data-from') || '0').replace(',', '.')) || 0;
    if (T3K.reduced) { render(target, true); return; }
    render(from, false);
    el._t3kCounter = function () {
      var start = null;
      function step(ts) {
        if (!start) start = ts;
        var p = clamp((ts - start) / dur, 0, 1);
        var e = 1 - Math.pow(1 - p, 3);
        if (p < 1) { render(from + (target - from) * e, false); raf(step); }
        else render(target, true);
      }
      raf(step);
    };
    if (!el.hasAttribute('data-reveal') && !el.hasAttribute('data-split')) observe(el);
  }

  /* ---------------------------------------------------------------------------
     06 MARQUEE
     --------------------------------------------------------------------------- */
  function initMarquee(el) {
    if (!el.children.length) return;
    var speed = Math.max(5, parseFloat(el.getAttribute('data-speed') || '40'));
    var dir = el.getAttribute('data-dir') === '-1' ? -1 : 1;
    var track = D.createElement('div');
    track.className = 'marquee__track';
    while (el.firstChild) track.appendChild(el.firstChild);
    el.appendChild(track);
    el.classList.add('marquee');
    if (dir < 0) el.classList.add('marquee--reverse');
    if (T3K.reduced) return;
    var clone = track.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    el.appendChild(clone);
    function measure() {
      var w = track.getBoundingClientRect().width;
      var cw = el.getBoundingClientRect().width;
      if (w <= 0 || cw <= 0) return;
      var copies = qsa('.marquee__track', el).length;
      while (w * copies < cw * 2 && copies < 8) {
        var c = track.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        el.appendChild(c);
        copies++;
      }
      el.style.setProperty('--marquee-dur', (w / speed).toFixed(2) + 's');
    }
    measure();
    on(W, 'resize', debounce(measure, 200));
    if (D.fonts && D.fonts.ready) D.fonts.ready.then(measure);
  }

  /* ---------------------------------------------------------------------------
     07 MAGNETIC / TILT / SPOTLIGHT
     --------------------------------------------------------------------------- */
  function initMagnetic(el) {
    if (!T3K.fine || T3K.reduced) return;
    var max = parseFloat(el.getAttribute('data-magnetic')) || 14;
    var r = null;
    el.classList.add('is-magnetic');
    on(el, 'pointerenter', function () { r = el.getBoundingClientRect(); });
    on(el, 'pointermove', function (e) {
      if (!r) r = el.getBoundingClientRect();
      var dx = clamp((e.clientX - (r.left + r.width / 2)) / (r.width / 2), -1, 1);
      var dy = clamp((e.clientY - (r.top + r.height / 2)) / (r.height / 2), -1, 1);
      el.style.transform = 'translate(' + (dx * max).toFixed(1) + 'px,' + (dy * max).toFixed(1) + 'px)';
    });
    on(el, 'pointerleave', function () { el.style.transform = ''; r = null; });
  }
  function initTilt(el) {
    if (!T3K.fine || T3K.reduced) return;
    var max = parseFloat(el.getAttribute('data-tilt')) || 10;
    var glare = D.createElement('span');
    glare.className = 'glare';
    glare.setAttribute('aria-hidden', 'true');
    el.appendChild(glare);
    var r = null, rafId = 0, pending = null, resetT = 0;
    function apply() {
      rafId = 0;
      if (!pending || !r) return;
      var px = clamp((pending.x - r.left) / r.width, 0, 1);
      var py = clamp((pending.y - r.top) / r.height, 0, 1);
      var rx = (0.5 - py) * max * 2;
      var ry = (px - 0.5) * max * 2;
      el.style.transform = 'perspective(900px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg)';
      el.style.setProperty('--gx', (px * 100).toFixed(1) + '%');
      el.style.setProperty('--gy', (py * 100).toFixed(1) + '%');
    }
    on(el, 'pointerenter', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      clearTimeout(resetT);
      r = el.getBoundingClientRect();
      el.style.transition = 'transform .12s ease-out';
      el.classList.add('is-tilting');
    });
    on(el, 'pointermove', function (e) {
      if (!r) return;
      pending = { x: e.clientX, y: e.clientY };
      if (!rafId) rafId = raf(apply);
    });
    on(el, 'pointerleave', function () {
      pending = null; r = null;
      el.classList.remove('is-tilting');
      el.style.transition = 'transform .7s cubic-bezier(.22,1,.36,1)';
      el.style.transform = '';
      resetT = setTimeout(function () { el.style.transition = ''; }, 720);
    });
  }
  function initSpotlight(el) {
    if (!T3K.fine) return;
    on(el, 'pointermove', function (e) {
      var r = el.getBoundingClientRect();
      el.style.setProperty('--mx', (e.clientX - r.left).toFixed(0) + 'px');
      el.style.setProperty('--my', (e.clientY - r.top).toFixed(0) + 'px');
    });
  }

  /* ---------------------------------------------------------------------------
     08 PARALLAX & SCROLL PROGRESS (single rAF loop)
     --------------------------------------------------------------------------- */
  var scrollItems = { parallax: [], progress: [] };
  var scrollTick = false;
  function scrollFrame() {
    scrollTick = false;
    var vh = W.innerHeight;
    if (scrollItems.parallax.length && !T3K.reduced && W.innerWidth >= 990) {
      scrollItems.parallax.forEach(function (it) {
        if (!it.el.isConnected) return;
        var r = it.el.getBoundingClientRect();
        var top = r.top - it.cur;
        if (top + r.height < -300 || top > vh + 300) return;
        var d = (top + r.height / 2 - vh / 2) * -it.f;
        it.cur = d;
        it.el.style.transform = 'translate3d(0,' + d.toFixed(1) + 'px,0)';
      });
    }
    scrollItems.progress.forEach(function (it) {
      if (!it.el.isConnected) return;
      var r = it.el.getBoundingClientRect();
      var p = clamp((vh - r.top) / (vh + r.height), 0, 1);
      if (Math.abs(p - it.last) > 0.002) {
        it.last = p;
        it.el.style.setProperty('--p', p.toFixed(3));
      }
    });
  }
  function requestScroll() { if (!scrollTick) { scrollTick = true; raf(scrollFrame); } }
  on(W, 'scroll', requestScroll, { passive: true });
  on(W, 'resize', debounce(requestScroll, 100));

  /* ---------------------------------------------------------------------------
     09 CUSTOM CURSOR
     --------------------------------------------------------------------------- */
  function initCursor() {
    var c = qs('#cursor');
    if (!c || !T3K.fine || T3K.reduced || T3K.design || T3K.settings.cursor === false) return;
    if (!once(c, 'cursor')) return;
    root.classList.add('has-cursor');
    var dot = qs('.cursor__dot', c), ring = qs('.cursor__ring', c), label = qs('.cursor__label', c);
    var x = W.innerWidth / 2, y = W.innerHeight / 2, rx = x, ry = y, active = false, rafId = 0;
    var labels = { view: S('cursorView', 'Vezi'), add: S('cursorAdd', 'Adaugă'), drag: S('cursorDrag', 'Trage') };
    function loop() {
      rx += (x - rx) * 0.18; ry += (y - ry) * 0.18;
      if (dot) dot.style.transform = 'translate(' + x + 'px,' + y + 'px) translate(-50%,-50%)';
      var rt = 'translate(' + rx.toFixed(1) + 'px,' + ry.toFixed(1) + 'px) translate(-50%,-50%)';
      if (ring) ring.style.transform = rt;
      if (label) label.style.transform = rt;
      if (Math.abs(x - rx) > 0.15 || Math.abs(y - ry) > 0.15) rafId = raf(loop); else rafId = 0;
    }
    on(D, 'pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') { c.classList.add('is-hidden'); return; }
      c.classList.remove('is-hidden');
      x = e.clientX; y = e.clientY;
      if (!active) { active = true; rx = x; ry = y; c.classList.add('is-active'); }
      if (!rafId) rafId = raf(loop);
    }, { passive: true });
    on(D, 'pointerdown', function () { c.classList.add('is-down'); });
    on(D, 'pointerup', function () { c.classList.remove('is-down'); });
    on(D, 'mouseleave', function () { c.classList.remove('is-active'); active = false; });
    on(D, 'mouseenter', function () { c.classList.add('is-active'); active = true; });
    on(D, 'pointerover', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var lab = t.closest('[data-cursor]');
      if (lab) {
        var k = lab.getAttribute('data-cursor');
        if (label) label.textContent = labels[k] || k;
        c.classList.add('has-label');
      } else c.classList.remove('has-label');
      var inter = t.closest('a,button,[role="button"],label,summary,input,select,textarea,.rail__track');
      c.classList.toggle('is-hover', !!inter && !lab);
    });
    on(W, 'touchstart', function () { c.classList.add('is-hidden'); root.classList.remove('has-cursor'); }, { passive: true });
  }

  /* ---------------------------------------------------------------------------
     10 CART API — T3K.cart.{add,change,update,note,get,refresh,open,close,toggle}
     Uses the Section Rendering API (cart-drawer,cart-icon-bubble) on every write.
     --------------------------------------------------------------------------- */
  var cart = T3K.cart = T3K.cart || {};
  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  cart.state = null;

  function fetchJSON(url, opts) {
    opts = opts || {};
    var headers = { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (opts.headers) Object.keys(opts.headers).forEach(function (k) { headers[k] = opts.headers[k]; });
    opts.headers = headers;
    opts.credentials = 'same-origin';
    return fetch(url, opts).then(function (res) {
      return res.text().then(function (txt) {
        var data = null;
        try { data = txt ? JSON.parse(txt) : null; } catch (e) { data = null; }
        if (!res.ok) {
          var err = new Error((data && (data.description || data.message)) || ('HTTP ' + res.status));
          err.status = res.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }
  function postJSON(url, payload) {
    return fetchJSON(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  }
  function jsUrl(route) { return /\.js$/.test(route) ? route : route + '.js'; }
  function withSections(payload) {
    payload.sections = SECTIONS;
    payload.sections_url = R.root;
    return payload;
  }
  function sectionsUrl() {
    var r = R.root || '/';
    return r + (r.indexOf('?') > -1 ? '&' : '?') + 'sections=' + SECTIONS;
  }
  function renderSections(sections) {
    if (!sections) return;
    var parser = new DOMParser();
    if (sections['cart-drawer']) {
      var doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      var fresh = doc.querySelector('[data-cart-drawer-content]');
      var cur = qs('[data-cart-drawer-content]');
      if (fresh && cur) {
        cur.innerHTML = fresh.innerHTML;
        T3K.init(cur);
      }
    }
    if (sections['cart-icon-bubble']) {
      var d2 = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      var inner = d2.querySelector('.shopify-section') || d2.body;
      qsa('[data-cart-bubble]').forEach(function (b) { b.innerHTML = inner.innerHTML; });
    }
  }
  function bumpCount() {
    qsa('.cart-count').forEach(function (el) {
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    });
  }
  function afterCartChange(c) {
    if (c && typeof c.item_count !== 'undefined') cart.state = c;
    dispatch('t3k:cart:updated', cart.state);
    return cart.state;
  }

  cart.get = function () {
    return fetchJSON(jsUrl(R.cart)).then(function (c) { cart.state = c; return c; });
  };
  cart.add = function (items) {
    if (!Array.isArray(items)) items = [items];
    items = items.map(function (it) {
      var o = { id: parseInt(it.id, 10), quantity: Math.max(1, parseInt(it.quantity, 10) || 1) };
      if (it.properties && Object.keys(it.properties).length) o.properties = it.properties;
      return o;
    }).filter(function (it) { return it.id > 0; });
    if (!items.length) return Promise.reject(new Error('no_items'));
    return postJSON(jsUrl(R.cartAdd), withSections({ items: items })).then(function (res) {
      if (res && res.sections) renderSections(res.sections);
      return cart.get();
    }).then(function (c) { bumpCount(); return afterCartChange(c); });
  };
  cart.change = function (key, qty) {
    return postJSON(jsUrl(R.cartChange), withSections({ id: key, quantity: Math.max(0, parseInt(qty, 10) || 0) })).then(function (c) {
      if (c && c.sections) renderSections(c.sections);
      return afterCartChange(c);
    });
  };
  cart.update = function (payload) {
    return postJSON(jsUrl(R.cartUpdate), withSections(payload || {})).then(function (c) {
      if (c && c.sections) renderSections(c.sections);
      return afterCartChange(c);
    });
  };
  cart.note = function (note) {
    return postJSON(jsUrl(R.cartUpdate), { note: note || '' }).then(function (c) { cart.state = c; return c; });
  };
  cart.refresh = function () {
    return fetch(sectionsUrl(), { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (sec) { renderSections(sec); return cart.get(); })
      .then(afterCartChange);
  };

  /* ---------------------------------------------------------------------------
     11 CART DRAWER, ADD-TO-CART HANDLERS, QTY, FREE-SHIPPING
     --------------------------------------------------------------------------- */
  var drawerUntrap = null, drawerLastFocus = null;
  function drawer() { return qs('#cart-drawer'); }
  cart.isOpen = function () { var d = drawer(); return !!(d && d.classList.contains('is-open')); };
  cart.open = function () {
    var d = drawer();
    if (!d || d.classList.contains('is-open')) return;
    closeMenu(); closeSearch(); closeLightbox();
    drawerLastFocus = D.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    D.body.classList.add('drawer-open');
    lockScroll();
    var panel = qs('.drawer__panel', d) || d;
    drawerUntrap = trapFocus(panel);
    focusFirst(panel, 120);
    dispatch('t3k:drawer:open');
    /* Plasa de siguranta: daca pagina a fost restaurata din cache (back/forward) sau cosul s-a schimbat
       in alta fila, continutul randat al sertarului poate fi vechi -> il aliniem cu serverul. */
    try {
      var shown = qs('[data-cart-count]', d);
      var shownN = shown ? parseInt(shown.textContent, 10) : NaN;
      fetch(T3K.routes.cart + '.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { if (c && typeof c.item_count === 'number' && c.item_count !== shownN) cart.refresh(); })
        .catch(function () { /* noop */ });
    } catch (e) { /* noop */ }
  };
  cart.close = function () {
    var d = drawer();
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    D.body.classList.remove('drawer-open');
    unlockScroll();
    if (drawerUntrap) { drawerUntrap(); drawerUntrap = null; }
    if (drawerLastFocus && drawerLastFocus.focus && D.contains(drawerLastFocus)) {
      try { drawerLastFocus.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
    dispatch('t3k:drawer:close');
  };
  cart.toggle = function () { if (cart.isOpen()) cart.close(); else cart.open(); };

  function setLoading(btn, isOn) {
    if (!btn) return;
    btn.classList.toggle('is-loading', !!isOn);
    if (isOn) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
  }
  function afterAdd(btn, silent) {
    /* the drawer opening is the confirmation; the toast would sit over its heading */
    if (!silent && !drawer()) T3K.toast(S('addedToCart', 'Adăugat în coș'), 'ok');
    if (btn) {
      btn.classList.add('is-success');
      setTimeout(function () { btn.classList.remove('is-success'); }, 1400);
    }
    cart.open();
  }
  /* Shopify answers cart errors in the store's API language (English here). Map the known
     messages to Romanian and never show the raw text to the customer. */
  function cartErrorText(err) {
    var raw = err && err.data && (err.data.description || err.data.message);
    var s = raw ? String(raw) : '';
    var m;
    if ((m = s.match(/only (\d+)/i)) && /added|availab/i.test(s)) {
      return S('partialAdd', 'Am adăugat doar {n} {unit}, atât avem în stoc.').replace('{n}', m[1]).replace('{unit}', m[1] === '1' ? 'bucată' : (parseInt(m[1], 10) >= 20 ? 'de bucăți' : 'bucăți'));
    }
    if (/maximum quantity|already in your cart|are in your cart|is in your cart|can(?:'|’)?t add more|cannot add more/i.test(s)) {
      return S('maxInCart', 'Ai deja în coș tot stocul disponibil pentru acest produs.');
    }
    if ((m = s.match(/can only add (\d+)/i))) {
      return S('maxQty', 'Poți adăuga cel mult {n} {unit} din acest produs.').replace('{n}', m[1]).replace('{unit}', parseInt(m[1], 10) >= 20 ? 'de bucăți' : 'bucăți');
    }
    if (/sold out|out of stock|unavailable|not available|no longer available/i.test(s)) {
      return S('soldOutNow', 'Produsul nu mai este disponibil momentan.');
    }
    return S('cartError', 'Nu am putut actualiza coșul. Încearcă din nou.');
  }
  /* 422 "Only N items were added..." = Shopify DID add what was in stock */
  function isPartialAdd(err) {
    var s = err && err.status === 422 && err.data && (err.data.description || err.data.message);
    return !!(s && /only \d+ .*added/i.test(String(s)));
  }
  cart.errorText = cartErrorText;
  cart.isPartialAdd = isPartialAdd;
  function cartError(err) {
    T3K.toast(cartErrorText(err), 'error', 4200);
  }
  /* partial add: refresh from the server, tell the customer what happened and open the drawer */
  function afterPartialAdd(btn, err, form) {
    return cart.refresh().catch(function () { /* noop */ }).then(function (c) {
      T3K.toast(cartErrorText(err), 'info', 4600);
      afterAdd(btn, true);
      if (form) dispatch('t3k:added', c || cart.state, form);
    });
  }
  function addFromButton(btn) {
    if (btn.classList.contains('is-loading') || btn.disabled) return;
    var id = btn.getAttribute('data-variant-id');
    var q = parseInt(btn.getAttribute('data-quantity') || '1', 10) || 1;
    if (!id) return;
    setLoading(btn, true);
    cart.add([{ id: id, quantity: q }])
      .then(function () { afterAdd(btn); })
      .catch(function (err) { return isPartialAdd(err) ? afterPartialAdd(btn, err) : cartError(err); })
      .then(function () { setLoading(btn, false); });
  }
  function addFromForm(form) {
    var btn = qs('[type="submit"]', form) || qs('button', form);
    if (btn && btn.classList.contains('is-loading')) return;
    var fd = new FormData(form);
    var id = fd.get('id');
    var q = parseInt(fd.get('quantity') || '1', 10) || 1;
    if (!id) { form.submit(); return; }
    var props = {};
    fd.forEach(function (v, k) {
      var m = k.match(/^properties\[(.+)\]$/);
      if (m && v !== '') props[m[1]] = v;
    });
    var item = { id: id, quantity: q };
    if (Object.keys(props).length) item.properties = props;
    setLoading(btn, true);
    cart.add([item])
      .then(function (c) { afterAdd(btn); dispatch('t3k:added', c, form); })
      .catch(function (err) { return isPartialAdd(err) ? afterPartialAdd(btn, err, form) : cartError(err); })
      .then(function () { setLoading(btn, false); });
  }
  function addMulti(btn) {
    if (btn.classList.contains('is-loading') || btn.disabled) return;
    var items = [];
    try { items = JSON.parse(btn.getAttribute('data-cart-add-multi') || '[]'); } catch (e) { items = []; }
    if (!Array.isArray(items) || !items.length) { T3K.toast(S('selectProducts', 'Alege produsele mai întâi.'), 'info'); return; }
    setLoading(btn, true);
    cart.add(items)
      .then(function (c) { afterAdd(btn); dispatch('t3k:added', c, btn); })
      .catch(function (err) { return isPartialAdd(err) ? afterPartialAdd(btn, err, btn) : cartError(err); })
      .then(function () { setLoading(btn, false); });
  }
  var qtyTimers = {};
  function lineOf(el) { return el.closest ? el.closest('[data-cart-line]') : null; }
  function stepQty(btn) {
    var wrap = btn.closest('.qty');
    var input = wrap ? qs('.qty__input', wrap) : null;
    if (!input) return;
    var step = parseInt(btn.getAttribute('data-qty-step'), 10) || 1;
    var min = parseInt(input.min || '0', 10);
    var max = input.max !== '' && input.max != null ? parseInt(input.max, 10) : Infinity;
    if (isNaN(max)) max = Infinity;
    var v = clamp((parseInt(input.value, 10) || 0) + step, min, max);
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
  function changeLine(input) {
    var key = input.getAttribute('data-line-key');
    if (!key) return;
    var q = Math.max(0, parseInt(input.value, 10) || 0);
    var line = lineOf(input);
    clearTimeout(qtyTimers[key]);
    qtyTimers[key] = setTimeout(function () {
      if (line) line.classList.add(q === 0 ? 'is-removing' : 'is-updating');
      var d = drawer();
      if (d) d.classList.add('is-loading');
      cart.change(key, q)
        .then(function () { if (q === 0) T3K.toast(S('removed', 'Produs eliminat'), 'info'); })
        .catch(function (err) { cartError(err); cart.refresh().catch(function () { /* noop */ }); })
        .then(function () { if (d) d.classList.remove('is-loading'); if (line) line.classList.remove('is-updating'); });
    }, 350);
  }
  function removeLine(btn) {
    var key = btn.getAttribute('data-line-key');
    if (!key) return;
    var line = lineOf(btn);
    if (line) line.classList.add('is-removing');
    cart.change(key, 0)
      .then(function () { T3K.toast(S('removed', 'Produs eliminat'), 'info'); })
      .catch(function (err) { cartError(err); if (line) line.classList.remove('is-removing'); cart.refresh().catch(function () { /* noop */ }); });
  }
  function renderFreeship(el, total) {
    var th = Number(T3K.freeShip) || 0;
    if (!th) { el.hidden = true; return; }
    var fill = qs('.freeship__fill', el), text = qs('.freeship__text', el);
    var left = th - total;
    var p = clamp(total / th * 100, 0, 100);
    el.style.setProperty('--p', p.toFixed(1) + '%');
    if (fill) fill.style.width = p.toFixed(1) + '%';
    if (left <= 0) {
      el.classList.add('is-free');
      if (text) text.innerHTML = '<strong>' + escapeHtml(S('freeShip', 'Ai livrare gratuită')) + '</strong>';
    } else {
      el.classList.remove('is-free');
      if (text) text.innerHTML = escapeHtml(S('freeShipRemaining', 'Mai ai {amount} până la livrare gratuită')).replace('{amount}', '<strong>' + escapeHtml(T3K.money(left)) + '</strong>');
    }
  }
  on(D, 't3k:cart:updated', function (e) {
    var c = e.detail;
    if (!c) return;
    qsa('[data-freeship]').forEach(function (el) { renderFreeship(el, c.total_price); });
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = c.item_count;
      el.classList.toggle('is-empty', c.item_count === 0);
    });
    qsa('[data-cart-total]').forEach(function (el) { el.textContent = T3K.money(c.total_price); });
    qsa('[data-cart-empty-toggle]').forEach(function (el) { el.hidden = c.item_count > 0; });
    qsa('[data-cart-full-toggle]').forEach(function (el) { el.hidden = c.item_count === 0; });
  });

  /* global delegated handlers */
  on(D, 'click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var el;
    if ((el = t.closest('[data-cart-toggle]'))) { e.preventDefault(); cart.toggle(); return; }
    if ((el = t.closest('[data-drawer-close]'))) { e.preventDefault(); cart.close(); return; }
    if ((el = t.closest('[data-menu-toggle]'))) { e.preventDefault(); toggleMenu(); return; }
    if ((el = t.closest('[data-search-toggle]'))) { e.preventDefault(); toggleSearch(); return; }
    if ((el = t.closest('[data-cart-add-multi]'))) { e.preventDefault(); addMulti(el); return; }
    if ((el = t.closest('button[data-cart-add],a[data-cart-add]')) && !el.closest('form[data-cart-add]')) { e.preventDefault(); addFromButton(el); return; }
    if ((el = t.closest('[data-cart-remove]'))) { e.preventDefault(); removeLine(el); return; }
    if ((el = t.closest('[data-qty-step]'))) { e.preventDefault(); stepQty(el); return; }
    if ((el = t.closest('[data-lightbox]'))) { e.preventDefault(); openLightbox(el); return; }
  });
  on(D, 'submit', function (e) {
    var f = e.target;
    if (f && f.matches && f.matches('form[data-cart-add]')) { e.preventDefault(); addFromForm(f); }
  });
  on(D, 'change', function (e) {
    var input = e.target;
    if (!input || !input.matches) return;
    if (input.matches('[data-line-qty]')) changeLine(input);
    else if (input.matches('[data-cart-note]')) cart.note(input.value).catch(function () { /* noop */ });
  });
  on(D, 'keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (qs('#lightbox.is-open')) { closeLightbox(); return; }
    if (cart.isOpen()) { cart.close(); return; }
    if (qs('#search-overlay.is-open')) { closeSearch(); return; }
    if (qs('#mobile-menu.is-open')) { closeMenu(); }
  });

  /* ---------------------------------------------------------------------------
     12 SEARCH OVERLAY + PREDICTIVE SEARCH
     --------------------------------------------------------------------------- */
  var searchUntrap = null, searchLast = null;
  function searchEl() { return qs('#search-overlay'); }
  function toggleSearch(force) {
    var el = searchEl();
    if (!el) return;
    var open = typeof force === 'boolean' ? force : !el.classList.contains('is-open');
    if (open) {
      if (el.classList.contains('is-open')) return;
      closeMenu(); cart.close();
      searchLast = D.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      D.body.classList.add('search-open');
      lockScroll();
      searchUntrap = trapFocus(el);
      var input = qs('.search__input', el);
      setTimeout(function () { if (input) { try { input.focus({ preventScroll: true }); } catch (e) { /* noop */ } } }, 140);
      qsa('[data-search-toggle]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    } else {
      if (!el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      D.body.classList.remove('search-open');
      unlockScroll();
      if (searchUntrap) { searchUntrap(); searchUntrap = null; }
      qsa('[data-search-toggle]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      if (searchLast && searchLast.focus && D.contains(searchLast)) { try { searchLast.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
    }
  }
  function closeSearch() { toggleSearch(false); }
  function sizedUrl(url, w) {
    try { return String(url).replace(/(\.(?:png|jpe?g|gif|webp|avif))(\?|$)/i, '_' + w + 'x' + w + '$1$2'); } catch (e) { return url; }
  }
  /* Same cleaning as snippets/product-title.liquid: drop the "Picături lichide" suffix, join the rest with " · " */
  function cleanProductTitle(t) {
    t = T3K.stripEmojiText(t).replace(/\s{2,}/g, ' ').trim();
    var names = [];
    t.split('•').forEach(function (part) {
      part = part.trim();
      if (!part) return;
      if (/^pic[ăa]turi lichide$/i.test(part)) return;
      part = part.replace(/\s*pic[ăa]turi lichide\s*/i, ' ').replace(/\s{2,}/g, ' ').trim();
      if (part) names.push(part);
    });
    return names.length ? names.join(' · ') : t;
  }
  function predictivePrice(p) {
    if (p == null || p === '') return '';
    var s = String(p);
    if (/^\d+(\.\d+)?$/.test(s)) return T3K.money(Math.round(parseFloat(s) * 100));
    return s;
  }
  function initSearch() {
    var el = searchEl();
    if (!el || !once(el, 'search')) return;
    var input = qs('.search__input', el), results = qs('.search__results', el), all = qs('.search__all', el), empty = qs('.search__empty', el);
    if (!input || !results) return;
    var lastQ = '', ctrl = null;
    function renderResults(prods, q) {
      if (!prods.length) {
        results.innerHTML = '';
        if (empty) { empty.hidden = false; empty.textContent = S('searchEmpty', 'Nu am găsit nimic pentru „{q}”.').replace('{q}', q); }
        if (all) all.hidden = true;
        return;
      }
      if (empty) empty.hidden = true;
      results.innerHTML = prods.map(function (p, i) {
        var img = p.featured_image && (p.featured_image.url || p.image);
        var price = predictivePrice(p.price);
        var cmp = (p.compare_at_price_max && Number(p.compare_at_price_max) > Number(p.price)) ? predictivePrice(p.compare_at_price_max) : '';
        return '<a class="search__result" href="' + escapeHtml(p.url) + '" style="--i:' + i + '">' +
          '<span class="search__thumb">' + (img ? '<img src="' + escapeHtml(sizedUrl(img, 160)) + '" alt="" width="64" height="64" loading="lazy">' : ICONS.drop) + '</span>' +
          '<span class="search__body"><span class="search__title">' + escapeHtml(cleanProductTitle(p.title)) + '</span>' +
          (p.available === false ? '<span class="search__sub">' + escapeHtml(S('soldOut', 'Stoc epuizat')) + '</span>' : '') + '</span>' +
          '<span class="search__price">' + (cmp ? '<s class="price__compare">' + escapeHtml(cmp) + '</s> ' : '') + escapeHtml(price) + '</span></a>';
      }).join('');
      if (all) {
        all.hidden = false;
        all.href = R.search + '?q=' + encodeURIComponent(q) + '&type=product';
      }
    }
    var run = debounce(function () {
      var q = input.value.trim();
      if (q === lastQ) return;
      lastQ = q;
      if (q.length < 2) { results.innerHTML = ''; if (all) all.hidden = true; if (empty) empty.hidden = true; return; }
      if (ctrl && ctrl.abort) ctrl.abort();
      ctrl = W.AbortController ? new AbortController() : null;
      var url = R.predictive.replace(/\.js(on)?$/, '') + '.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor';
      el.classList.add('is-searching');
      fetch(url, { signal: ctrl ? ctrl.signal : undefined, headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var prods = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          renderResults(prods, q);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          results.innerHTML = '';
          if (all) all.hidden = true;
          if (empty) { empty.hidden = false; empty.textContent = S('searchError', 'Căutarea nu a mers acum. Apasă Enter ca să vezi toate rezultatele.'); }
        })
        .then(function () { el.classList.remove('is-searching'); });
    }, 200);
    on(input, 'input', run);
    on(input, 'keydown', function (e) { if (e.key === 'ArrowDown') { var f = qs('.search__result', results); if (f) { e.preventDefault(); f.focus(); } } });
  }

  /* ---------------------------------------------------------------------------
     12b NEWSLETTER FORMS — themed validation message (aria-invalid + .form-msg)
     --------------------------------------------------------------------------- */
  function initNewsletterForm(form) {
    var input = qs('input[type="email"]', form);
    if (!input) return;
    var group = input.closest('.input-group') || input;
    function msgEl(create) {
      var m = qs('[data-live-error]', form);
      if (!m && create) {
        m = D.createElement('p');
        m.className = 'form-msg is-error form-msg--live';
        m.setAttribute('data-live-error', '');
        m.setAttribute('role', 'alert');
        group.parentNode.insertBefore(m, group.nextSibling);
      }
      return m;
    }
    function showErr(text) {
      input.setAttribute('aria-invalid', 'true');
      group.classList.add('is-invalid');
      var m = msgEl(true);
      m.textContent = text;
      m.hidden = false;
    }
    function clearErr() {
      input.removeAttribute('aria-invalid');
      group.classList.remove('is-invalid');
      var m = msgEl(false);
      if (m) m.hidden = true;
    }
    on(input, 'invalid', function (e) {
      e.preventDefault();
      showErr(input.value.trim()
        ? S('emailInvalid', 'Scrie o adresă de email validă, de exemplu nume@exemplu.ro.')
        : S('emailRequired', 'Scrie adresa ta de email ca să te abonezi.'));
      try { input.focus({ preventScroll: true }); } catch (err) { /* noop */ }
    });
    on(input, 'input', function () { if (input.getAttribute('aria-invalid')) clearErr(); });
  }

  /* ---------------------------------------------------------------------------
     13 MOBILE MENU
     --------------------------------------------------------------------------- */
  var menuUntrap = null, menuLast = null;
  function toggleMenu(force) {
    var m = qs('#mobile-menu');
    if (!m) return;
    var open = typeof force === 'boolean' ? force : !m.classList.contains('is-open');
    var burgers = qsa('[data-menu-toggle]');
    if (open) {
      if (m.classList.contains('is-open')) return;
      closeSearch(); cart.close();
      menuLast = D.activeElement;
      m.classList.add('is-open');
      m.setAttribute('aria-hidden', 'false');
      D.body.classList.add('menu-open');
      lockScroll();
      burgers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      menuUntrap = trapFocus(m);
      focusFirst(m, 160);
    } else {
      if (!m.classList.contains('is-open')) return;
      m.classList.remove('is-open');
      m.setAttribute('aria-hidden', 'true');
      D.body.classList.remove('menu-open');
      unlockScroll();
      burgers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      if (menuUntrap) { menuUntrap(); menuUntrap = null; }
      if (menuLast && menuLast.focus && D.contains(menuLast)) { try { menuLast.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
    }
  }
  function closeMenu() { toggleMenu(false); }
  on(W, 'resize', debounce(function () { if (W.innerWidth >= 990 && qs('#mobile-menu.is-open')) closeMenu(); }, 150));

  /* ---------------------------------------------------------------------------
     14 HEADER — glass after 40px, hides on scroll down, shows on scroll up
     --------------------------------------------------------------------------- */
  function initHeader() {
    var h = qs('.header');
    if (!h || !once(h, 'header')) return;
    var lastY = W.pageYOffset || 0, ticking = false;
    function update() {
      ticking = false;
      var y = W.pageYOffset || 0;
      h.classList.toggle('is-scrolled', y > 40 || h.hasAttribute('data-solid'));
      var b = D.body.classList;
      if (b.contains('menu-open') || b.contains('search-open') || b.contains('drawer-open')) { lastY = y; return; }
      var dy = y - lastY;
      if (y > 160 && dy > 4) h.classList.add('is-hidden');
      else if (dy < -4 || y <= 160) h.classList.remove('is-hidden');
      lastY = y;
    }
    on(W, 'scroll', function () { if (!ticking) { ticking = true; raf(update); } }, { passive: true });
    on(D, 'focusin', function (e) { if (h.contains(e.target)) h.classList.remove('is-hidden'); });
    update();
  }

  /* ---------------------------------------------------------------------------
     15 LOADER — once per session (sessionStorage t3k_loaded), ~1.1s
     --------------------------------------------------------------------------- */
  function initLoader() {
    var l = qs('#loader');
    if (!l) { dispatch('t3k:loaded'); return; }
    var finished = false;
    function done() {
      if (finished) return;
      finished = true;
      l.classList.add('is-done');
      root.classList.add('is-loaded');
      try { sessionStorage.setItem('t3k_loaded', '1'); } catch (e) { /* noop */ }
      dispatch('t3k:loaded');
      setTimeout(function () { if (l.parentNode) l.parentNode.removeChild(l); }, 900);
    }
    if (T3K.reduced || T3K.design || root.classList.contains('is-loaded') || T3K.settings.loader === false) { done(); return; }
    var conn = navigator.connection || {};
    var minT = (T3K.fine === false || conn.saveData) ? 500 : 1150;
    var t0 = Date.now();
    function go() { setTimeout(done, Math.max(0, minT - (Date.now() - t0))); }
    if (D.readyState === 'complete') go(); else { on(W, 'load', go); setTimeout(go, 2600); }
  }

  /* ---------------------------------------------------------------------------
     16 PAGE TRANSITIONS — curtain fallback, View Transitions when supported
     --------------------------------------------------------------------------- */
  function initTransitions() {
    if (root.classList.contains('is-entering')) {
      setTimeout(function () { root.classList.remove('is-entering'); }, 1100);
    }
    on(W, 'pageshow', function (e) {
      root.classList.remove('is-leaving');
      if (e.persisted) root.classList.remove('is-entering');
    });
    if (T3K.settings.transitions === false || T3K.reduced || T3K.design) return;
    var vt = ('startViewTransition' in D) && W.CSS && CSS.supports && CSS.supports('view-transition-name', 'none');
    if (vt) root.classList.add('has-vt');
    if (!vt && !T3K.fine) return;
    on(D, 'click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return;
      var href = a.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#' || /^(mailto:|tel:|javascript:|sms:)/i.test(href)) return;
      var url;
      try { url = new URL(a.href, location.href); } catch (err) { return; }
      if (url.origin !== location.origin) return;
      if (/^\/(cart|checkout|account\/logout|\d+\/checkouts)/.test(url.pathname) || /checkout/.test(url.pathname)) return;
      if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
      if (a.closest('[data-cart-toggle],[data-menu-toggle],[data-search-toggle]')) return;
      e.preventDefault();
      try { sessionStorage.setItem('t3k_leave', '1'); } catch (err) { /* noop */ }
      closeMenu(); closeSearch(); cart.close();
      root.classList.add('is-leaving');
      setTimeout(function () { location.href = url.href; }, vt ? 0 : 320);
    });
  }

  /* ---------------------------------------------------------------------------
     17 ACCORDION — animates <details> height inside [data-accordion]
     --------------------------------------------------------------------------- */
  function initAccordion(container) {
    var single = container.getAttribute('data-accordion') === 'single';
    qsa('details', container).forEach(function (d) {
      if (!once(d, 'acc')) return;
      var sum = qs('summary', d);
      if (!sum) return;
      var body = sum.nextElementSibling;
      if (!body) {
        body = D.createElement('div');
        body.className = 'acc__body';
        while (sum.nextSibling) body.appendChild(sum.nextSibling);
        d.appendChild(body);
      }
      body.classList.add('acc__body');
      var anim = null;
      function closeDetails(det, bod) {
        var h = bod.offsetHeight;
        det.classList.add('is-closing');
        var a = bod.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 360, easing: 'cubic-bezier(.65,0,.35,1)' });
        a.onfinish = function () { det.open = false; det.classList.remove('is-closing'); };
        return a;
      }
      on(sum, 'click', function (e) {
        e.preventDefault();
        if (T3K.reduced || !body.animate) {
          d.open = !d.open;
          if (single && d.open) qsa('details[open]', container).forEach(function (o) { if (o !== d) o.open = false; });
          return;
        }
        if (anim) { anim.cancel(); anim = null; }
        if (d.open && !d.classList.contains('is-closing')) {
          anim = closeDetails(d, body);
          anim.addEventListener('finish', function () { anim = null; });
        } else {
          d.classList.remove('is-closing');
          d.open = true;
          if (single) {
            qsa('details[open]', container).forEach(function (o) {
              if (o === d || o.classList.contains('is-closing')) return;
              var ob = qs('.acc__body', o);
              if (ob && ob.animate) closeDetails(o, ob); else o.open = false;
            });
          }
          var h2 = body.offsetHeight;
          anim = body.animate([{ height: '0px', opacity: 0 }, { height: h2 + 'px', opacity: 1 }], { duration: 460, easing: 'cubic-bezier(.22,1,.36,1)' });
          anim.addEventListener('finish', function () { anim = null; });
        }
      });
    });
  }

  /* ---------------------------------------------------------------------------
     18 LIGHTBOX — [data-lightbox] (optional value = large image URL)
     --------------------------------------------------------------------------- */
  var lbEl = null, lbUntrap = null, lbLast = null;
  function ensureLightbox() {
    if (lbEl) return lbEl;
    lbEl = D.createElement('div');
    lbEl.id = 'lightbox';
    lbEl.className = 'lightbox';
    lbEl.setAttribute('role', 'dialog');
    lbEl.setAttribute('aria-modal', 'true');
    lbEl.setAttribute('aria-label', S('imageView', 'Imagine mărită'));
    lbEl.innerHTML = '<div class="lightbox__glow" aria-hidden="true"></div><img class="lightbox__img" alt="" width="1200" height="1200">' +
      '<button type="button" class="btn btn--icon btn--glass lightbox__close" aria-label="' + escapeHtml(S('close', 'Închide')) + '">' + ICONS.close + '</button>';
    D.body.appendChild(lbEl);
    on(lbEl, 'click', function (e) {
      if (e.target === lbEl || e.target.closest('.lightbox__close')) closeLightbox();
    });
    return lbEl;
  }
  function openLightbox(trigger) {
    var img = trigger.tagName === 'IMG' ? trigger : qs('img', trigger);
    var src = trigger.getAttribute('data-lightbox') || (trigger.tagName === 'A' ? trigger.getAttribute('href') : '') || (img ? (img.currentSrc || img.src) : '');
    if (!src) return;
    var lb = ensureLightbox();
    var target = qs('.lightbox__img', lb);
    target.src = src;
    target.alt = trigger.getAttribute('alt') || (img ? img.alt : '') || '';
    lbLast = D.activeElement;
    lb.classList.add('is-open');
    lockScroll();
    lbUntrap = trapFocus(lb);
    focusFirst(lb, 60);
  }
  function closeLightbox() {
    if (!lbEl || !lbEl.classList.contains('is-open')) return;
    lbEl.classList.remove('is-open');
    unlockScroll();
    if (lbUntrap) { lbUntrap(); lbUntrap = null; }
    if (lbLast && lbLast.focus && D.contains(lbLast)) { try { lbLast.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
  }

  /* ---------------------------------------------------------------------------
     19 RAIL — snap scroller with prev/next, dots, drag-to-scroll on desktop
     --------------------------------------------------------------------------- */
  function initRail(rail) {
    var track = qs('.rail__track', rail);
    if (!track) return;
    var prev = qs('.rail__prev', rail), next = qs('.rail__next', rail), dots = qs('.rail__dots', rail);
    function gap() { var cs = getComputedStyle(track); return parseFloat(cs.columnGap || cs.gap) || 0; }
    function itemW() {
      var it = qs('.rail__item', track);
      return it ? it.getBoundingClientRect().width + gap() : track.clientWidth * 0.8;
    }
    function buildDots() {
      if (!dots) return;
      var items = qsa('.rail__item', track);
      dots.innerHTML = '';
      items.forEach(function (_, i) {
        var b = D.createElement('button');
        b.type = 'button';
        b.className = 'rail__dot';
        b.setAttribute('aria-label', (S('slide', 'Elementul') + ' ' + (i + 1)));
        if (dots.getAttribute('aria-hidden') === 'true') b.tabIndex = -1;
        on(b, 'click', function () { track.scrollTo({ left: i * itemW(), behavior: T3K.reduced ? 'auto' : 'smooth' }); });
        dots.appendChild(b);
      });
    }
    function update() {
      var max = track.scrollWidth - track.clientWidth - 2;
      var atStart = track.scrollLeft <= 2, atEnd = track.scrollLeft >= max;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
      rail.classList.toggle('is-scrollable', max > 2);
      if (dots) {
        var idx = Math.round(track.scrollLeft / itemW());
        qsa('.rail__dot', dots).forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
      }
    }
    on(prev, 'click', function () { track.scrollBy({ left: -itemW(), behavior: T3K.reduced ? 'auto' : 'smooth' }); });
    on(next, 'click', function () { track.scrollBy({ left: itemW(), behavior: T3K.reduced ? 'auto' : 'smooth' }); });
    on(track, 'scroll', debounce(update, 60), { passive: true });
    on(W, 'resize', debounce(update, 200));
    buildDots();
    update();
    if (T3K.fine) {
      var down = false, startX = 0, startL = 0, moved = false;
      on(track, 'pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = false; startX = e.clientX; startL = track.scrollLeft;
      });
      on(W, 'pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) { moved = true; track.classList.add('is-dragging'); }
        if (moved) track.scrollLeft = startL - dx;
      });
      var up = function () {
        if (!down) return;
        down = false;
        if (moved) setTimeout(function () { track.classList.remove('is-dragging'); moved = false; }, 60);
      };
      on(W, 'pointerup', up);
      on(W, 'pointercancel', up);
      on(track, 'click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
      on(track, 'dragstart', function (e) { e.preventDefault(); });
    }
  }

  /* ---------------------------------------------------------------------------
     20 ANCHORS — smooth scroll with header offset
     --------------------------------------------------------------------------- */
  on(D, 'click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
    if (!a || e.defaultPrevented) return;
    var id = a.getAttribute('href').slice(1);
    if (!id) return;
    var target = D.getElementById(id) || qs('[name="' + id.replace(/"/g, '') + '"]');
    if (!target) return;
    e.preventDefault();
    closeMenu();
    var top = target.getBoundingClientRect().top + (W.pageYOffset || 0) - headerH() - 16;
    W.scrollTo({ top: Math.max(0, top), behavior: T3K.reduced ? 'auto' : 'smooth' });
    try { history.pushState(null, '', '#' + id); } catch (err) { /* noop */ }
  });

  /* ---------------------------------------------------------------------------
     21 INIT & BOOT
     --------------------------------------------------------------------------- */
  T3K.init = function (scope) {
    var r = scope && scope.nodeType ? scope : D;
    root.classList.add('t3k-ready');
    scopeAll(r, '[data-stagger]').forEach(function (p) {
      if (!once(p, 'stagger')) return;
      var step = parseInt(p.getAttribute('data-stagger') || '80', 10);
      qsa('[data-reveal]', p).filter(function (k) { return k.parentElement && k.parentElement.closest('[data-stagger]') === p; })
        .forEach(function (k, i) {
          var own = parseInt(k.getAttribute('data-delay') || '0', 10);
          k.style.setProperty('--delay', (own + i * step) + 'ms');
        });
    });
    scopeAll(r, '[data-reveal]').forEach(function (el) {
      if (!once(el, 'reveal')) return;
      var d = el.getAttribute('data-delay');
      if (d && !el.style.getPropertyValue('--delay')) el.style.setProperty('--delay', d + 'ms');
      if (T3K.reduced) { el.classList.add('is-in'); if (el._t3kCounter) el._t3kCounter(); return; }
      observe(el);
    });
    scopeAll(r, '[data-split]').forEach(function (el) {
      if (!once(el, 'split')) return;
      if (T3K.reduced) { el.classList.add('is-in'); return; }
      try { splitText(el); } catch (e) { /* keep plain text */ }
      if (!el.hasAttribute('data-reveal')) observe(el);
    });
    scopeAll(r, '[data-counter]').forEach(function (el) { if (once(el, 'counter')) initCounter(el); });
    scopeAll(r, '[data-marquee]').forEach(function (el) { if (once(el, 'marquee')) initMarquee(el); });
    scopeAll(r, '[data-magnetic]').forEach(function (el) { if (once(el, 'magnetic')) initMagnetic(el); });
    scopeAll(r, '[data-tilt]').forEach(function (el) { if (once(el, 'tilt')) initTilt(el); });
    scopeAll(r, '[data-spotlight]').forEach(function (el) { if (once(el, 'spotlight')) initSpotlight(el); });
    scopeAll(r, '[data-parallax]').forEach(function (el) {
      if (once(el, 'parallax')) scrollItems.parallax.push({ el: el, f: parseFloat(el.getAttribute('data-parallax')) || 0.2, cur: 0 });
    });
    scopeAll(r, '[data-scroll-progress]').forEach(function (el) {
      if (once(el, 'progress')) scrollItems.progress.push({ el: el, last: -1 });
    });
    scopeAll(r, '[data-accordion]').forEach(function (el) { if (once(el, 'accordion')) initAccordion(el); });
    scopeAll(r, '.rail').forEach(function (el) { if (once(el, 'rail')) initRail(el); });
    scopeAll(r, '.rte,[data-strip-emoji]').forEach(function (el) { if (once(el, 'emoji')) T3K.stripEmoji(el); });
    scopeAll(r, '[data-freeship]').forEach(function (el) { if (cart.state) renderFreeship(el, cart.state.total_price); });
    scopeAll(r, 'form[data-newsletter]').forEach(function (el) { if (once(el, 'newsletter')) initNewsletterForm(el); });
    initHeader();
    initSearch();
    requestScroll();
    dispatch('t3k:init', { scope: r });
  };
  T3K.util = { qs: qs, qsa: qsa, on: on, debounce: debounce, clamp: clamp, escapeHtml: escapeHtml, lockScroll: lockScroll, unlockScroll: unlockScroll, trapFocus: trapFocus, once: once };
  T3K.menu = { open: function () { toggleMenu(true); }, close: closeMenu, toggle: function () { toggleMenu(); } };
  T3K.search = { open: function () { toggleSearch(true); }, close: closeSearch, toggle: function () { toggleSearch(); } };
  T3K.lightbox = { open: openLightbox, close: closeLightbox };

  function boot() {
    if (T3K.design) root.classList.add('is-design-mode');
    T3K.init(D);
    initLoader();
    initCursor();
    initTransitions();
    on(D, 'shopify:section:load', function (e) { T3K.init(e.target); });
    on(D, 'shopify:section:select', function (e) {
      T3K.init(e.target);
      if (e.target && qs('#cart-drawer', e.target)) cart.open();
    });
    on(D, 'shopify:section:deselect', function (e) {
      if (e.target && qs('#cart-drawer', e.target)) cart.close();
    });
    on(D, 'shopify:block:select', function (e) {
      var el = e.target;
      if (el && el.closest) {
        var det = el.closest('details');
        if (det) det.open = true;
        var sec = el.closest('[data-reveal]');
        if (sec) sec.classList.add('is-in');
      }
    });
    if (location.hash === '#cart' || /[?&]cart=open/.test(location.search)) cart.open();
    dispatch('t3k:ready');
  }
  if (D.readyState === 'loading') on(D, 'DOMContentLoaded', boot); else boot();
})();
